<template>
  <div class="grid grid-cols-2 gap-2 pt-1 normal-case">
    <InfuseTag
      v-for="capability in Capability"
      :key="`evse-${evseId}-capability-${capability}`"
      :clickable="!readOnly"
      :variant="isSelectedCapability(capability) ? 'primary' : 'neutral'"
      @click="selectCapability(capability)"
    >
      <font-awesome-icon
        v-if="isSelectedCapability(capability)"
        :fixed-width="true"
        :icon="['fas', 'check']"
        class="mr-1"
      />
      <font-awesome-icon v-else :icon="['fas', 'xmark']" class="mr-1" />
      {{ t(`capabilities.options.${capability}`) }}
    </InfuseTag>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Capability } from '@infuse/api/cpms/config';
import { InfuseTag } from '@infuse/ui/components';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

interface Emits {
  (e: 'select', capability: Capability): void;
}
const emit = defineEmits<Emits>();

interface Props {
  evseId: number;
  readOnly: boolean;
  selectedCapabilities: Capability[];
}
const props = withDefaults(defineProps<Props>(), {
  readOnly: false
});

const { t } = useI18n();

const capabilities = ref<Capability[]>([]);

onMounted(() => {
  capabilities.value = props.selectedCapabilities;
});

watch(
  // This being a function + 'deep: true' makes this 'watch' able to track changes in an array.
  () => props.selectedCapabilities,
  async (inputCapabilities) => {
    // If an external source updates the capabilities (e.g. model reload),
    // then reflect those changes in this component as well.
    capabilities.value = inputCapabilities;
  },
  { immediate: false, deep: true }
);

const selectCapability = (capability: Capability) => {
  if (!props.readOnly) {
    emit('select', capability);
  }
};

const isSelectedCapability = (capability: Capability): boolean => {
  return !!capabilities.value?.find((c: Capability) => c === capability);
};
</script>

<i18n>
{
  "en": {
    "capabilities": {
      "options": {
        "CHARGING_PROFILE_CAPABLE": "Charging profiles",
        "CHARGING_PREFERENCES_CAPABLE": "Charging preferences",
        "CHIP_CARD_SUPPORT": "Chip card payment",
        "CONTACTLESS_CARD_SUPPORT": "Contactless payment",
        "CREDIT_CARD_PAYABLE": "Credit card payment",
        "DEBIT_CARD_PAYABLE": "Debit card payment",
        "PED_TERMINAL": "Pin-code entry device",
        "REMOTE_START_STOP_CAPABLE": "Remote sessions",
        "RESERVABLE": "Reservable",
        "RFID_READER": "RFID reader",
        "START_SESSION_CONNECTOR_REQUIRED": "Remote sessions require connector",
        "TOKEN_GROUP_CAPABLE": "Token group support",
        "UNLOCK_CAPABLE": "Remote unlock"
      }
    }
  },
  "nl": {
    "capabilities": {
      "options": {
        "CHARGING_PROFILE_CAPABLE": "Laadprofielen",
        "CHARGING_PREFERENCES_CAPABLE": "Laadvoorkeuren",
        "CHIP_CARD_SUPPORT": "Betalen met chip-kaart",
        "CONTACTLESS_CARD_SUPPORT": "Contactloos betalen",
        "CREDIT_CARD_PAYABLE": "Betalen met credit card",
        "DEBIT_CARD_PAYABLE": "Betalen met debit card",
        "PED_TERMINAL": "Invoerapparaat pincode",
        "REMOTE_START_STOP_CAPABLE": "Sessiebeheer op afstand",
        "RESERVABLE": "Reserveerbaar",
        "RFID_READER": "RFID lezer",
        "START_SESSION_CONNECTOR_REQUIRED": "Sessiebeheer op afstand vereist aansluiting",
        "TOKEN_GROUP_CAPABLE": "Ondersteuning voor tokengroepen",
        "UNLOCK_CAPABLE": "Ontgrendelen op afstand"
      }
    }
  }
}
</i18n>
